/**
 * Clients slice (PX-W6.2) — the L2 client register: list, detail, and the
 * create/edit/archive mutations.
 *
 * ⚠ The sparse-PUT trap lives here as a TYPE-level guarantee: `updateClient`
 * and the archive/restore flows accept only a complete `ClientInput`. The
 * server saves the patch wholesale (blank-only fallbacks are name/status), so
 * every write must carry the loaded nested objects or they null out.
 */

import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { selectActiveOrgId, type SessionState } from '@/features/auth/slices/sessionSlice'
import { isApiError, toApiError, type ApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'

import * as clientsHttp from './clients.http'
import type { ApiClientProject, ApiClientRow, ClientInput } from './types'

/**
 * Preserve the canonical ApiError: the shared transport's interceptor already
 * maps AxiosErrors to it, so re-mapping through `toApiError` would degrade a
 * real 403 to the network fallback. Only strangers go through the mapper.
 */
const rejected = (error: unknown): ApiError => (isApiError(error) ? error : toApiError(error))

const orgKey = (getState: () => unknown): string | null =>
  selectActiveOrgId(getState() as { session: SessionState })

/** What an org-keyed thunk returns: rows plus the org they loaded for. */
interface OrgRows<T> {
  orgId: string | null
  rows: T
}

export const fetchClients = createAsyncThunk<OrgRows<ApiClientRow[]>, void, {
  rejectValue: ApiError
}>(
  'clients/fetchClients',
  async (_, { getState, rejectWithValue }) => {
    try {
      const rows = await clientsHttp.clientsHttp.list()
      return { orgId: orgKey(getState), rows }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (_, { getState }) =>
      shouldFetch((getState() as { clients: ClientsState }).clients.cache, orgKey(getState))
  }
)

export const fetchClient = createAsyncThunk<OrgRows<ApiClientRow>, string, {
  rejectValue: ApiError
}>(
  'clients/fetchClient',
  async (clientId, { getState, rejectWithValue }) => {
    try {
      const rows = await clientsHttp.clientsHttp.get(clientId)
      return { orgId: orgKey(getState), rows }
    } catch (error) {
      return rejectWithValue(rejected(error))
    }
  },
  {
    condition: (clientId, { getState }) => {
      const { detail } = (getState() as { clients: ClientsState }).clients
      return detail.id !== clientId || shouldFetch(detail.cache, orgKey(getState))
    }
  }
)

export const createClient = createAsyncThunk<ApiClientRow, ClientInput, {
  rejectValue: ApiError
}>('clients/createClient', async (input, { rejectWithValue }) => {
  try {
    return await clientsHttp.clientsHttp.create(input)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

/**
 * Edit an existing client. The arg carries the COMPLETE editable payload
 * (name, status, profile, contacts, commercialTerms, dataRoomRoot) — the
 * thunk refuses a blank name rather than letting the server fall back.
 */
export const updateClient = createAsyncThunk<
  ApiClientRow,
  { clientId: string; input: ClientInput },
  { rejectValue: ApiError }
>('clients/updateClient', async ({ clientId, input }, { rejectWithValue }) => {
  try {
    return await clientsHttp.clientsHttp.update(clientId, input)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export const fetchClientProjects = createAsyncThunk<
  ApiClientProject[],
  string,
  { rejectValue: ApiError }
>('clients/fetchClientProjects', async (clientId, { rejectWithValue }) => {
  try {
    return await clientsHttp.clientsHttp.projects(clientId)
  } catch (error) {
    return rejectWithValue(rejected(error))
  }
})

export interface ClientsState {
  byId: Record<string, ApiClientRow>
  allIds: string[]
  cache: FetchCache
  error: ApiError | null
  detail: { id: string | null; cache: FetchCache; error: ApiError | null }
  /** Linked projects per client id (rendered in the detail panel). */
  projectsByClientId: Record<string, ApiClientProject[]>
  /** The most recent mutation failure, for inline form errors (409 …). */
  mutationError: ApiError | null
}

const initialState: ClientsState = {
  byId: {},
  allIds: [],
  cache: initialFetchCache,
  error: null,
  detail: { id: null, cache: initialFetchCache, error: null },
  projectsByClientId: {},
  mutationError: null
}

const mergeClient = (state: ClientsState, row: ApiClientRow): void => {
  if (!(row.id in state.byId)) state.allIds.push(row.id)
  state.byId[row.id] = row
}

export const clientsSlice = createSlice({
  name: 'clients',
  initialState,
  reducers: {
    /** Clear the surfaced mutation error (the form was edited again). */
    mutationErrorDismissed(state) {
      state.mutationError = null
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchClients.pending, (state) => {
        fetchStarted(state.cache)
        state.error = null
      })
      .addCase(fetchClients.fulfilled, (state, action: PayloadAction<OrgRows<ApiClientRow[]>>) => {
        fetchSucceeded(state.cache, action.payload.orgId)
        state.byId = {}
        state.allIds = []
        for (const row of action.payload.rows) mergeClient(state, row)
      })
      .addCase(fetchClients.rejected, (state, action) => {
        fetchFailed(state.cache)
        state.error = action.payload ?? null
      })
      .addCase(fetchClient.pending, (state, action) => {
        fetchStarted(state.detail.cache)
        state.detail.id = action.meta.arg
        state.detail.error = null
      })
      .addCase(fetchClient.fulfilled, (state, action: PayloadAction<OrgRows<ApiClientRow>>) => {
        fetchSucceeded(state.detail.cache, action.payload.orgId)
        mergeClient(state, action.payload.rows)
      })
      .addCase(fetchClient.rejected, (state, action) => {
        fetchFailed(state.detail.cache)
        state.detail.error = action.payload ?? null
      })
      .addCase(createClient.pending, (state) => {
        state.mutationError = null
      })
      .addCase(createClient.fulfilled, (state, action: PayloadAction<ApiClientRow>) => {
        mergeClient(state, action.payload)
      })
      .addCase(createClient.rejected, (state, action) => {
        state.mutationError = action.payload ?? null
      })
      .addCase(updateClient.pending, (state) => {
        state.mutationError = null
      })
      .addCase(updateClient.fulfilled, (state, action: PayloadAction<ApiClientRow>) => {
        mergeClient(state, action.payload)
      })
      .addCase(updateClient.rejected, (state, action) => {
        state.mutationError = action.payload ?? null
      })
      .addCase(fetchClientProjects.fulfilled, (state, action) => {
        state.projectsByClientId[action.meta.arg] = action.payload
      })
  }
})

export const { mutationErrorDismissed } = clientsSlice.actions
